import React, { useState } from 'react';

import { Box, Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';
import { openToastError } from 'src/use-cases/toast/useToast';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useNavigate } from 'react-router-dom';
import { useTourContext } from 'src/presentations/guided-tours';

import IconReceipt from 'src/assets/images/icons/receipt-alt.svg';
import IconReceipt2 from 'src/assets/images/icons/document-code.svg';
import IconMessage from 'src/assets/images/icons/message-question.svg';
import IconTask from 'src/assets/images/icons/document-upload.svg';
import IconTaskPrimary from 'src/assets/images/icons/document-upload-primary.svg';
import IconLogs from 'src/assets/images/icons/activity-log.svg';
import IconArrowDown from 'src/assets/images/icons/chevron-down.svg';
import {
  InvoiceFileUrl,
  useGetCreditNoteFileURL,
  useGetFileUrl,
  useGetInvoiceFileUrls,
  useGetInvoiceReport,
  useInvoiceSummaryFromParams,
} from 'src/use-cases/invoice/useInvoices';
import { CustomTooltip } from 'src/presentations/components/atom/customtooltip';
import IconTaskSquare from 'src/assets/images/icons/task-square.svg';
import IconTrash from 'src/assets/images/icons/trash.svg';

import { useDeleteInvoices } from 'src/use-cases/invoice/useDeleteInvoice';
import { store } from 'src/infra/stores/store';
import { DELETE_MODE_KEY } from 'src/infra/stores/reducer/deleteModeSlice';
import { downloadZip } from 'src/utils/downloadFile';

import ModalActivityLog from '../activityLog/ActivityLogModal';
// AttachmentMenu is now handled inline
import { useContactSelector } from '../../../molecules/contact/ContactSelector';
import { ActionDetailTopInfo } from './TopInfo';
import { ActionDetailMainActions } from './TopActions';
import { useShipmentSelector } from '../ShipmentSelector';
import StatusChip from '../../dashboard/StatusChip';
import { useUploadModalSelector } from '../UploadDocument/UploadModalSelector';

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiMenu-paper': {
    borderRadius: '8px!important',
    width: '290px',
    marginTop: '8px',
    border: '1px solid #DFDFDF',
    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
    [theme.breakpoints.down(1520)]: {
      width: '260px',
    },
    '& .MuiList-root': {
      [theme.breakpoints.down(1520)]: {
        paddingTop: '4px',
        paddingBottom: '4px',
      },
    },
    '& .MuiListItemIcon-root': {
      [theme.breakpoints.down(1520)]: {
        minWidth: '30px',
      },
    },
  },
}));

interface StyledTooltipProps extends MuiTooltipProps {
  className?: string;
}

const Tooltip = styled(({ className, ...props }: StyledTooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />)(() => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#2B2B2B',
    color: 'white',
  },
  '& .MuiTooltip-arrow': {
    color: '#2B2B2B',
  },
}));

// Helper function to get file type from file name (same logic as useGetInvoiceFileUrls)
const getFileType = (fileName?: string) => {
  if (!fileName) return 'UNKNOWN';
  const extension = fileName.split('.').pop() || 'unknown';
  return extension.toUpperCase();
};

export const ActionDetailTopMain = () => {
  const { data: invoiceSummary } = useInvoiceSummaryFromParams();
  const { shipment: shipmentDetail } = useShipmentSelector();
  const navigate = useNavigate();
  const tour = useTourContext().appTour.registerTour('actionDetails');

  const [moreActionEl, setMoreActionEl] = useState<HTMLButtonElement | null>(null);
  const [fileMenuEl, setFileMenuEl] = useState<HTMLButtonElement | null>(null);
  const [isShowActivityLog, setIsShowActivityLog] = useState<boolean>(false);

  const { setIsShowContact, setInvoice } = useContactSelector();
  const { showUpload } = useUploadModalSelector();
  const { refetch } = useGetFileUrl(invoiceSummary?.id);
  const { data: fileUrls } = useGetInvoiceFileUrls(invoiceSummary?.id);
  const { refetch: refetchCreditNote } = useGetCreditNoteFileURL(invoiceSummary?.invoiceDispute?.id);
  const { refetch: refetchInvoiceReport } = useGetInvoiceReport(invoiceSummary?.id);

  // Check if this is NRCF: contractId === null OR shippingServiceId === null
  const isNrcf = !shipmentDetail?.contractId || !shipmentDetail?.shippingServiceId;

  const onClickMoreAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMoreActionEl(event.currentTarget);
  };

  const onCloseMoreAction = () => {
    setMoreActionEl(null);
  };

  const onOpenFileMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    // If there's only one file, open it directly instead of showing dropdown
    if (fileUrls && fileUrls.length === 1) {
      onOpenInvoiceFile(fileUrls[0].fileUrl, fileUrls[0].fileType);
      return;
    }

    // If multiple files, show dropdown
    setFileMenuEl(event.currentTarget);
  };

  const onCloseFileMenu = () => {
    setFileMenuEl(null);
  };

  const onShowOriginalInvoice = async () => {
    // Legacy fallback if new API doesn't work
    setMoreActionEl(null);
    const result = await refetch();
    if (result?.data) {
      window.open(result?.data, '_blank');
    }
  };

  const onOpenInvoiceFile = (fileUrl: string, fileType?: string) => {
    setFileMenuEl(null);

    // If file is PDF, open in new tab
    // Otherwise download it or handle differently based on type
    if (!fileType || fileType === 'PDF') {
      window.open(fileUrl, '_blank');
    } else {
      // For non-PDF files, create a download link and click it
      const downloadLink = document.createElement('a');
      downloadLink.href = fileUrl;
      downloadLink.download = `invoice.${fileType.toLowerCase()}`; // Set a default filename
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const onDownloadConfiguredRateCard = () => {
    setMoreActionEl(null);

    if (shipmentDetail?.shipmentInvoiceId) {
      window.open(`/configured-rate-card/${shipmentDetail.shipmentInvoiceId}`, '_blank');
    } else {
      openToastError(new Error('Shipment invoice ID not found'));
    }
  };

  const onDownloadInvoiceReport = async () => {
    const result = await refetchInvoiceReport();

    downloadZip(result, 'invoice-report');
  };

  const onUploadNewDocument = () => {
    showUpload();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onShowCreditNote = async () => {
    setMoreActionEl(null);
    const result = await refetchCreditNote();
    if (result?.data) {
      window.open(result.data, '_blank');
    }
  };

  const onOpenActivityLog = () => {
    setIsShowActivityLog(true);
  };

  const onCloseActivityLog = () => {
    setIsShowActivityLog(false);
  };

  const { mutateAsync: deleteInvoice } = useDeleteInvoices();

  const state = store.getState();
  const enableDeleteMode = state[DELETE_MODE_KEY]?.enable ?? false;

  const handleDelete = (invoiceNumber: string) => {
    deleteInvoice({ invoiceNumbers: [invoiceNumber] })
      .then(() => {
        // Navigate to homepage after successful deletion
        navigate('/');
      })
      .catch((error) => {
        console.error('Error deleting invoice:', error);
        openToastError(new Error('Failed to delete invoice'));
      });
  };

  const onClickContact = () => {
    const invoiceContact = {
      modality: invoiceSummary?.modality,
      provider: invoiceSummary?.freightForwarder?.code,
      invoiceNumber: invoiceSummary?.invoiceNumber,
      id: invoiceSummary?.id,
      mblHbl: shipmentDetail ? shipmentDetail?.mblHbl : '',
      status: invoiceSummary?.status,
      shipmentInvoiceId: shipmentDetail?.shipmentInvoiceId,
      shipmentNumber: shipmentDetail?.shipmentDetail?.shipmentNumber,
    };
    setIsShowContact(true);
    setInvoice(invoiceContact);
  };

  return (
    <div className='bg-white border-b-1 border-neutral-20'>
      <Box className='bg-white flex items-center justify-between px-16 xl:px-30 py-14 xl:py-16'>
        <ActionDetailTopInfo invoiceSummary={invoiceSummary} />
        <div id={tour.registerStep('toolbarIcons')} className='flex items-center gap-8 xl:gap-12'>
          <button
            type='button'
            className='h-[34px] xl:h-36 px-6 flex items-center gap-6 justify-center border-1  xl:border-2 border-neutral-20 rounded-4'
            aria-label='View Invoice'
            onClick={onOpenFileMenu}
          >
            <Tooltip title={fileUrls?.length > 1 ? 'View Invoice Files' : 'View Invoice'} placement='bottom' arrow>
              <img src={IconReceipt} alt='' className='w-20' />
            </Tooltip>
            {/* Only show dropdown arrow if there are multiple files */}
            {fileUrls && fileUrls.length > 1 && <img src={IconArrowDown} alt='' className='w-14' />}
          </button>
          {/* Hide View Configured Rate Card button for NRCF (contractId === null OR shippingServiceId === null) */}
          {!isNrcf && (
            <button
              type='button'
              className='w-[34px] xl:w-36 h-[34px] xl:h-36 flex items-center justify-center border-1 xl:border-2 border-neutral-20 rounded-4'
              aria-label='View Configured Rate Card'
              onClick={onDownloadConfiguredRateCard}
            >
              <Tooltip title='View Configured Rate Card' placement='bottom' arrow>
                <img src={IconReceipt2} alt='' className='w-20' />
              </Tooltip>
            </button>
          )}
          <button
            type='button'
            className='w-[34px] xl:w-36 h-[34px] xl:h-36 flex items-center justify-center border-1 xl:border-2 border-neutral-20 rounded-4'
            aria-label='View Invoice Report'
            onClick={onDownloadInvoiceReport}
          >
            <Tooltip title='View Invoice Report' placement='bottom' arrow>
              <img src={IconTaskSquare} alt='' className='w-20' />
            </Tooltip>
          </button>

          <button
            type='button'
            className='w-[34px] xl:w-36 h-[34px] xl:h-36 flex items-center justify-center border-1 xl:border-2 border-neutral-20 rounded-4'
            aria-label='Activity Log'
            onClick={onOpenActivityLog}
          >
            <Tooltip title='Activity Log' placement='bottom' arrow>
              <img src={IconLogs} alt='' className='w-20' />
            </Tooltip>
          </button>
          <button
            type='button'
            className='h-36 px-6 flex items-center gap-6 justify-center border-1 xl:border-2 border-neutral-20 rounded-4'
            aria-label='Attach Documents'
            onClick={onClickMoreAction}
          >
            <Tooltip title='Attach Documents' placement='bottom' arrow>
              <img src={IconTask} alt='' className='w-20' />
            </Tooltip>

            <img src={IconArrowDown} alt='' className='w-14' />
          </button>
          <button
            type='button'
            className='text-18 w-[34px] xl:w-36 h-[34px] xl:h-36 flex items-center justify-center border-1 xl:border-2 border-neutral-20 rounded-4'
            aria-label='Contact Freya'
            onClick={onClickContact}
          >
            <Tooltip title='Contact Freya' placement='bottom' arrow>
              <img src={IconMessage} alt='' className='w-20' />
            </Tooltip>
          </button>
          {enableDeleteMode && (
            <button
              type='button'
              className='text-18 w-[34px] xl:w-36 h-[34px] xl:h-36 flex items-center justify-center border-1 xl:border-2 border-neutral-20 rounded-4'
              aria-label='Delete Invoice'
              onClick={() => {
                const confirmed = confirm(
                  `Are you sure you want to delete invoice "${invoiceSummary?.invoiceNumber}"?\n\nWarning: All files, invoices and documents associated with this invoice number will be permanently deleted. This action cannot be undone.`
                );
                if (confirmed) {
                  handleDelete(invoiceSummary?.invoiceNumber || '');
                }
              }}
            >
              <Tooltip title='Delete Invoice' placement='bottom' arrow>
                <img src={IconTrash} alt='' className='w-20' style={{ filter: 'brightness(0.5)' }} />
              </Tooltip>
            </button>
          )}

          <ActionDetailMainActions />
        </div>

        <StyledMenu
          open={!!moreActionEl}
          anchorEl={moreActionEl}
          onClose={onCloseMoreAction}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          style={{ marginTop: '4px' }}
        >
          <MenuItem onClick={onUploadNewDocument}>
            <ListItemIcon>
              <img src={IconTaskPrimary} alt='' className='w-20 xl:w-24' />
            </ListItemIcon>
            <ListItemText>
              <span className='text-14 xl:text-16 text-primary font-medium'>Upload Documents</span>
            </ListItemText>
          </MenuItem>
          {invoiceSummary?.invoiceAttachments?.length > 0 && (
            <>
              <li key='header' className='pt-18 pb-8 mb-4 px-16 border-b border-neutral-20'>
                <h4 className='text-12 xl:text-14 text-neutral-40'>Attachments</h4>
              </li>
              {invoiceSummary.invoiceAttachments.map((attachment) => (
                <MenuItem
                  key={attachment.id}
                  onClick={() => {
                    setMoreActionEl(null);
                    window.open(`/file-attachment/${invoiceSummary.id}/${attachment.id}`, '_blank');
                  }}
                >
                  <ListItemText>
                    <div className='flex items-center gap-2'>
                      <span className='font-bold text-neutral-40 pr-3 py-1 text-sm'>{getFileType(attachment.fileName)}</span>
                      <CustomTooltip title={attachment.fileName || 'Attachment'}>
                        <div className='max-w-[130px] overflow-hidden text-ellipsis whitespace-nowrap'>
                          <span className='font-medium text-sm'>{attachment.fileName || 'Attachment'}</span>
                        </div>
                      </CustomTooltip>
                    </div>
                  </ListItemText>
                </MenuItem>
              ))}
            </>
          )}
        </StyledMenu>

        {/* File View Menu */}
        <StyledMenu
          open={!!fileMenuEl}
          anchorEl={fileMenuEl}
          onClose={onCloseFileMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          style={{ marginTop: '4px' }}
        >
          {fileUrls && fileUrls.length > 0 ? (
            <>
              {fileUrls.length > 1 && (
                <MenuItem onClick={() => {}} disabled sx={{ opacity: '1!important', borderBottom: '1px solid #E5E7EB' }}>
                  <ListItemText>
                    <div className='flex items-center gap-2'>
                      <span className='font-medium text-neutral-40 pr-3 py-1 text-sm'>Uploaded Invoice Files</span>
                    </div>
                  </ListItemText>
                </MenuItem>
              )}
              {fileUrls.map((file: InvoiceFileUrl, index: number) => (
                <MenuItem key={`file-${file.fileType}-${file.fileName}-${file.fileUrl}`} onClick={() => onOpenInvoiceFile(file.fileUrl, file.fileType)}>
                  <ListItemText>
                    <div className='flex items-center gap-2'>
                      <span className='font-bold text-neutral-40 pr-3 py-1 text-sm'>{file.fileType}</span>
                      <CustomTooltip title={file.fileName}>
                        <div className='max-w-[130px] overflow-hidden text-ellipsis whitespace-nowrap'>
                          <span className='font-medium text-sm'>{file.fileName}</span>
                        </div>
                      </CustomTooltip>
                      {file?.status === 'DUPLICATE' && (
                        <div className='ml-auto'>
                          <StatusChip status='DUPLICATE' size='small' />
                        </div>
                      )}
                    </div>
                  </ListItemText>
                </MenuItem>
              ))}
            </>
          ) : (
            <MenuItem onClick={onShowOriginalInvoice}>
              <ListItemText>
                <span className='font-medium'>View Original Invoice</span>
              </ListItemText>
            </MenuItem>
          )}
        </StyledMenu>

        {/* AttachmentMenu components have been moved inside the dropdown menu */}
        <ModalActivityLog invoiceSummary={invoiceSummary} open={isShowActivityLog} onClose={onCloseActivityLog} />
      </Box>
    </div>
  );
};
